import { useState } from "react"

import { ExclamationTriangleIcon } from "@heroicons/react/20/solid"
import { clsx } from "clsx"

import type {
  SolarLightReachDto,
  SolarLightReachOfferDto,
  SolarLightReachSelectedOfferDto,
} from "~/api/dto/solar-lightreach.dto"
import useGetSolarLightReachPricing from "~/api/hooks/useGetSolarLightReachPricing"
import useSelectSolarLightReachOffer from "~/api/hooks/useSelectSolarLightReachOffer"
import useSolarLightReach from "~/api/hooks/useSolarLightReach"

import {
  CHOOSE_LIGHTREACH_OFFER_BUSY_LABEL,
  CHOOSE_LIGHTREACH_OFFER_ERROR,
  CHOOSE_LIGHTREACH_OFFER_LABEL,
  GET_LIGHTREACH_PRICING_BUSY_LABEL,
  GET_LIGHTREACH_PRICING_ERROR,
  GET_LIGHTREACH_PRICING_LABEL,
  LIGHTREACH_EMPTY_OFFERS_PROMPT,
  LIGHTREACH_FETCHED_PREFIX,
  LIGHTREACH_FIRST_YEAR_PAYMENT_LABEL,
  LIGHTREACH_KWH_RATE_LABEL,
  LIGHTREACH_LOAD_ERROR,
  LIGHTREACH_MARGIN_LABEL,
  LIGHTREACH_MARGIN_NOTE,
  LIGHTREACH_NO_OFFERS_YET_PROMPT,
  LIGHTREACH_NOT_AVAILABLE_PROMPT,
  LIGHTREACH_OFFER_CHOSEN_LABEL,
  LIGHTREACH_OFFER_HEADING,
  LIGHTREACH_PRICING_OUT_OF_DATE,
  LIGHTREACH_QUOTE_STATUS_LABEL,
  LIGHTREACH_SELECTED_OFFER_HEADING,
  LIGHTREACH_SUGGESTED_LABEL,
  LIGHTREACH_SYSTEM_COST_LABEL,
  LIGHTREACH_TIER_LABEL,
  SHOW_SUGGESTED_LIGHTREACH_TIERS_LABEL,
  showAllLightReachTiersLabel,
  solarLightReachErrorMessage,
} from "./solar-lightreach-copy"
import { SolarLockedNote } from "./SolarLockedNote"
import {
  TPO_PRODUCT_TYPE_LABEL,
  tpoTermsText,
} from "../BatteryOnlyProposal/tpo-terms-copy"
import {
  formatUsdExact,
  formatUsdPerKwh,
} from "../BatteryOnlyProposal/view-helpers"
import {
  btnLink,
  btnPrimary,
  monthlyUsd,
  Pill,
  sectionLabelClass,
} from "../ProposalSetup/financing/financing-chrome"
import { LightReachOfferRow } from "../ProposalSetup/financing/lightreach/LightReachOfferPicker"

/** PPA first, then lease; a type LightReach returned nothing for is omitted. */
const PRODUCT_TYPE_ORDER: readonly SolarLightReachOfferDto[`productType`][] = [
  `ppa`,
  `lease`,
]

const perWatt = (rate: number): string => `${formatUsdExact(rate)}/W`

const fetchedAtLabel = (iso: string): string =>
  new Intl.DateTimeFormat(`en-US`, {
    month: `short`,
    day: `numeric`,
    hour: `numeric`,
    minute: `2-digit`,
  }).format(new Date(iso))

/** "$0.149/kWh · Tier $3.10/W": what one tier adds to its terms. */
const tierDetail = (offer: {
  kwhRate: number | null
  ppwRate: number
}): string =>
  [
    offer.kwhRate != null ? formatUsdPerKwh(offer.kwhRate) : null,
    `${LIGHTREACH_TIER_LABEL} ${perWatt(offer.ppwRate)}`,
  ]
    .filter(Boolean)
    .join(` · `)

/** Suggested tiers first, then by term, escalator and tier. */
const byPresentation = (
  left: SolarLightReachOfferDto,
  right: SolarLightReachOfferDto,
): number =>
  Number(right.suggested) - Number(left.suggested) ||
  left.termYears - right.termYears ||
  left.escalationRate - right.escalationRate ||
  left.ppwRate - right.ppwRate

/**
 * The offers grouped PPA / lease. Each type shows its suggested tiers (the
 * ones nearest Powur's current $/W) and the selected one; the rest wait
 * behind Show all tiers. A type with nothing suggested shows every tier.
 */
const offerGroups = (
  offers: readonly SolarLightReachOfferDto[],
  showAll: boolean,
): {
  groups: {
    productType: SolarLightReachOfferDto[`productType`]
    offers: SolarLightReachOfferDto[]
  }[]
  hidden: number
} => {
  let hidden = 0
  const groups = PRODUCT_TYPE_ORDER.map((productType) => {
    const all = offers
      .filter((offer) => offer.productType === productType)
      .sort(byPresentation)
    const anySuggested = all.some((offer) => offer.suggested)
    const shown =
      showAll || !anySuggested
        ? all
        : all.filter((offer) => offer.suggested || offer.selected)
    hidden += all.length - shown.length
    return { productType, offers: shown }
  }).filter((group) => group.offers.length > 0)
  return { groups, hidden }
}

/** The tier to start on: the selected one, else the first suggested as listed (PPA first). */
const defaultOffer = (
  offers: readonly SolarLightReachOfferDto[],
): SolarLightReachOfferDto | null =>
  offers.find((offer) => offer.selected) ??
  offerGroups(offers, true)
    .groups.flatMap((group) => group.offers)
    .find((offer) => offer.suggested) ??
  null

const QUOTE_STATUS_TONE = {
  current: `ok`,
  stale: `warn`,
  unavailable: `danger`,
} as const

/**
 * The selected offer as the seller reads it: its label and terms, LightReach's
 * year-one payment, $/kWh and tier, whether it is current, and what it set on
 * Powur's side (the back-solved margin and LightReach's system cost). Every
 * figure is the server's.
 */
const SelectedOfferCard = ({
  offer,
}: {
  offer: SolarLightReachSelectedOfferDto
}): JSX.Element => (
  <section
    aria-label={LIGHTREACH_SELECTED_OFFER_HEADING}
    data-testid="solar-lightreach-selected-offer"
    className={clsx(
      `flex flex-col gap-3 rounded-lg border px-3.5 py-3`,
      offer.quoteStatus === `unavailable`
        ? `border-bp-danger/40 bg-bp-danger/5`
        : `border-bp-border bg-bp-elevated`,
    )}
  >
    <div className="flex flex-wrap items-start justify-between gap-3">
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[length:var(--step--2)] text-bp-text-dim">
          {LIGHTREACH_SELECTED_OFFER_HEADING}
        </span>
        <span className="text-[length:var(--step--1)] font-semibold text-bp-text">
          {offer.label}
        </span>
        <span className="text-[length:var(--step--2)] text-bp-text-muted">
          {`${TPO_PRODUCT_TYPE_LABEL[offer.productType]} · ${tpoTermsText({
            termYears: offer.termYears,
            escalationRate: offer.escalatorPercent / 100,
          })}`}
        </span>
      </span>
      <span data-testid="solar-lightreach-quote-status">
        <Pill tone={QUOTE_STATUS_TONE[offer.quoteStatus]}>
          {LIGHTREACH_QUOTE_STATUS_LABEL[offer.quoteStatus]}
        </Pill>
      </span>
    </div>
    <dl className="grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-4 gap-y-1.5 text-[length:var(--step--2)]">
      <dt className="text-bp-text-dim">{LIGHTREACH_FIRST_YEAR_PAYMENT_LABEL}</dt>
      <dd className="text-bp-text tabular-nums">
        {offer.firstYearMonthlyPayment != null
          ? `${monthlyUsd.format(offer.firstYearMonthlyPayment)}/mo`
          : `—`}
      </dd>
      {offer.kwhRate != null ? (
        <>
          <dt className="text-bp-text-dim">{LIGHTREACH_KWH_RATE_LABEL}</dt>
          <dd className="text-bp-text tabular-nums">
            {formatUsdPerKwh(offer.kwhRate)}
          </dd>
        </>
      ) : null}
      <dt className="text-bp-text-dim">{LIGHTREACH_TIER_LABEL}</dt>
      <dd className="text-bp-text tabular-nums">{perWatt(offer.ppwRate)}</dd>
      <dt className="text-bp-text-dim">{LIGHTREACH_MARGIN_LABEL}</dt>
      <dd
        data-testid="solar-lightreach-margin"
        className="text-bp-text tabular-nums"
      >
        {perWatt(offer.margin)}
        <span className="block text-bp-text-dim">{LIGHTREACH_MARGIN_NOTE}</span>
      </dd>
      {offer.totalSystemCost != null ? (
        <>
          <dt className="text-bp-text-dim">{LIGHTREACH_SYSTEM_COST_LABEL}</dt>
          <dd
            data-testid="solar-lightreach-system-cost"
            className="text-bp-text tabular-nums"
          >
            {formatUsdExact(offer.totalSystemCost)}
          </dd>
        </>
      ) : null}
    </dl>
    {offer.quoteStatus !== `current` && offer.staleReason != null ? (
      <p className="text-[length:var(--step--2)] text-bp-gold">
        {offer.staleReason}
      </p>
    ) : null}
  </section>
)

/** The out-of-date banner: the offers or the selected offer no longer match today's pricing. */
const StaleBanner = (): JSX.Element => (
  <div
    role="status"
    data-testid="solar-lightreach-stale"
    className="flex items-start gap-2 rounded-lg border border-bp-gold/40 bg-bp-gold/10 px-3.5 py-3 text-[length:var(--step--2)] text-bp-text"
  >
    <ExclamationTriangleIcon
      aria-hidden="true"
      className="mt-0.5 size-4 shrink-0 text-bp-gold"
    />
    <span>{LIGHTREACH_PRICING_OUT_OF_DATE}</span>
  </div>
)

/**
 * LightReach's half of the Loan Product step. Get LightReach pricing sends the
 * design to LightReach and stores its offers; choosing one back-solves Powur's
 * margin to LightReach's price for that tier and prices PowurOS once. Every
 * payment, rate and margin is the server's; nothing is calculated here.
 */
export const SolarLightReachOfferPanel = ({
  proposalId,
}: {
  proposalId: string
}): JSX.Element => {
  const lightReachQuery = useSolarLightReach(proposalId)
  const getPricing = useGetSolarLightReachPricing()
  const selectOffer = useSelectSolarLightReachOffer()
  const [draftOfferId, setDraftOfferId] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)
  const [chosen, setChosen] = useState(false)

  const read: SolarLightReachDto | null = lightReachQuery.data ?? null
  if (read == null) {
    return (
      <div
        className="flex flex-col gap-3"
        data-testid="solar-lightreach-offer-panel"
      >
        <h3 className={sectionLabelClass}>{LIGHTREACH_OFFER_HEADING}</h3>
        {lightReachQuery.isError ? (
          <div className="flex flex-col gap-2">
            <p
              role="alert"
              className="text-[length:var(--step--2)] text-bp-danger"
            >
              {LIGHTREACH_LOAD_ERROR}
            </p>
            <button
              type="button"
              onClick={() => void lightReachQuery.refetch()}
              className={btnLink}
            >
              Retry
            </button>
          </div>
        ) : null}
      </div>
    )
  }

  const { offers, selectedOffer, actions } = read
  const rows = offers.status === `available` ? offers.offers : []
  const { groups, hidden } = offerGroups(rows, showAll)
  const busy =
    getPricing.isLoading || selectOffer.isLoading || lightReachQuery.isFetching
  const chosenOffer =
    rows.find((offer) => offer.offerId === draftOfferId) ?? defaultOffer(rows)
  const alreadySelected =
    chosenOffer?.selected === true && selectedOffer?.quoteStatus === `current`
  const stale =
    offers.status === `stale` || selectedOffer?.quoteStatus === `stale`

  const runGetPricing = (): void => {
    selectOffer.reset()
    setChosen(false)
    setDraftOfferId(null)
    getPricing.mutate({ proposalId })
  }

  const runSelect = (): void => {
    if (chosenOffer == null || busy) return
    getPricing.reset()
    selectOffer.mutate(
      { proposalId, body: { offerId: chosenOffer.offerId } },
      {
        onSuccess: () => {
          setDraftOfferId(null)
          setChosen(true)
        },
      },
    )
  }

  return (
    <div
      className="flex flex-col gap-4"
      data-testid="solar-lightreach-offer-panel"
    >
      <h3 className={sectionLabelClass}>{LIGHTREACH_OFFER_HEADING}</h3>
      <p className="text-[length:var(--step--2)] text-bp-text-muted">
        {read.note}
      </p>

      {read.financingLocked && read.financingLockReason ? (
        <SolarLockedNote testId="solar-lightreach-locked">
          {read.financingLockReason}
        </SolarLockedNote>
      ) : null}

      {!read.available ? (
        <div
          role="status"
          data-testid="solar-lightreach-unavailable"
          className="flex flex-col gap-1 text-[length:var(--step--2)] text-bp-gold"
        >
          {read.eligibilityReasons.length > 0 ? (
            <ul className="list-disc pl-4">
              {read.eligibilityReasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          ) : (
            <p>{LIGHTREACH_NOT_AVAILABLE_PROMPT}</p>
          )}
        </div>
      ) : null}

      {stale ? <StaleBanner /> : null}

      {selectedOffer != null ? (
        <SelectedOfferCard offer={selectedOffer} />
      ) : null}

      <div className="flex flex-col items-start gap-2">
        <button
          type="button"
          data-testid="solar-lightreach-get-pricing"
          disabled={!actions.getPricing.allowed || busy}
          onClick={runGetPricing}
          className={offers.status === `available` ? btnLink : btnPrimary}
        >
          {getPricing.isLoading
            ? GET_LIGHTREACH_PRICING_BUSY_LABEL
            : GET_LIGHTREACH_PRICING_LABEL}
        </button>
        {!actions.getPricing.allowed && actions.getPricing.reason ? (
          <p
            data-testid="solar-lightreach-get-pricing-reason"
            className="text-[length:var(--step--2)] text-bp-text-muted"
          >
            {actions.getPricing.reason}
          </p>
        ) : null}
        {offers.fetchedAt != null ? (
          <p className="text-[length:var(--step--3)] text-bp-text-dim">
            {`${LIGHTREACH_FETCHED_PREFIX} ${fetchedAtLabel(offers.fetchedAt)}`}
          </p>
        ) : null}
        {getPricing.isError ? (
          <p
            role="alert"
            className="text-[length:var(--step--2)] text-bp-danger"
          >
            {solarLightReachErrorMessage(
              getPricing.error,
              GET_LIGHTREACH_PRICING_ERROR,
            )}
          </p>
        ) : null}
      </div>

      {offers.status === `none` && read.available ? (
        <p
          role="status"
          className="text-[length:var(--step--2)] text-bp-text-muted"
        >
          {LIGHTREACH_NO_OFFERS_YET_PROMPT}
        </p>
      ) : offers.status === `empty` ? (
        <p
          role="status"
          className="text-[length:var(--step--2)] text-bp-text-muted"
        >
          {LIGHTREACH_EMPTY_OFFERS_PROMPT}
        </p>
      ) : null}

      {groups.map((group) => (
        <fieldset
          key={group.productType}
          className="flex flex-col gap-2"
          data-testid={`solar-lightreach-offer-group-${group.productType}`}
        >
          <legend className="mb-2 text-[length:var(--step--2)] font-semibold tracking-[0.12em] text-bp-text-muted uppercase">
            {TPO_PRODUCT_TYPE_LABEL[group.productType]}
          </legend>
          {group.offers.map((offer) => (
            <LightReachOfferRow
              key={offer.offerId}
              offer={offer}
              value={offer.offerId}
              checked={offer.offerId === chosenOffer?.offerId}
              unsaved={offer.offerId === chosenOffer?.offerId && !offer.selected}
              disabled={!actions.selectOffer.allowed || busy}
              detail={tierDetail(offer)}
              badge={
                offer.suggested ? (
                  <Pill tone="muted">{LIGHTREACH_SUGGESTED_LABEL}</Pill>
                ) : null
              }
              onSelect={() => {
                selectOffer.reset()
                setChosen(false)
                setDraftOfferId(offer.offerId)
              }}
            />
          ))}
        </fieldset>
      ))}

      {hidden > 0 || (showAll && rows.some((offer) => !offer.suggested)) ? (
        <button
          type="button"
          data-testid="solar-lightreach-show-all-tiers"
          aria-expanded={showAll}
          onClick={() => setShowAll((current) => !current)}
          className={btnLink}
        >
          {showAll
            ? SHOW_SUGGESTED_LIGHTREACH_TIERS_LABEL
            : showAllLightReachTiersLabel(hidden)}
        </button>
      ) : null}

      {rows.length > 0 &&
      !actions.selectOffer.allowed &&
      actions.selectOffer.reason ? (
        <p className="text-[length:var(--step--2)] text-bp-text-muted">
          {actions.selectOffer.reason}
        </p>
      ) : null}

      {rows.length > 0 && actions.selectOffer.allowed ? (
        <div className="flex flex-col items-start gap-2">
          <button
            type="button"
            data-testid="solar-lightreach-choose-offer"
            disabled={chosenOffer == null || alreadySelected || busy}
            onClick={runSelect}
            className={btnPrimary}
          >
            {selectOffer.isLoading
              ? CHOOSE_LIGHTREACH_OFFER_BUSY_LABEL
              : CHOOSE_LIGHTREACH_OFFER_LABEL}
          </button>
          {chosen && !selectOffer.isLoading ? (
            <p
              role="status"
              className="text-[length:var(--step--2)] font-medium text-bp-success"
            >
              {LIGHTREACH_OFFER_CHOSEN_LABEL}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* A refusal may have left the offers stale, so it outlives the list. */}
      {selectOffer.isError ? (
        <p
          role="alert"
          data-testid="solar-lightreach-select-error"
          className="text-[length:var(--step--2)] text-bp-danger"
        >
          {solarLightReachErrorMessage(
            selectOffer.error,
            CHOOSE_LIGHTREACH_OFFER_ERROR,
          )}
        </p>
      ) : null}
    </div>
  )
}
